import { useCallback, useMemo, useState } from 'react';

import { usePendingDisplay } from '@/shared/hooks/usePendingDisplay';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import {
  useFocusMode,
  useRememberWorkflow,
} from '@/shared/hooks/usePageChrome';
import { WorkflowSkeleton } from './WorkflowSkeleton';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { usePreferencesStore } from '@/shared/store/preferences';
import type { ImportViolation } from '@/lib/api/generated/model';
import { NotFoundState } from '@/shared/components/NotFoundState';
import { useSession } from '@/lib/auth/session';

import { DraftProvider } from '../builder/DraftProvider';
import { useDraft } from '../builder/useDraftContext';
import { addStep } from '../builder/draftOps';
import { fieldErrorsFor, violationTargets } from '../builder/fieldErrors';
import type { ProblemFocus } from '../builder/problemFocus';
import { useUndoShortcuts } from '../builder/useUndoShortcuts';
import { useUnsavedGuard } from '../builder/useUnsavedGuard';
import { useBuilderBackup } from '../builder/useBuilderBackup';
import { clearBuilderBackup } from '../builder/builderBackupStore';
import {
  parseSelection,
  resolveSelection,
  serialiseSelection,
} from '../builder/selection';
import type { BuilderSelection } from '../builder/selection';
import { useDraftWrites } from '../hooks/useDraftWrites';
import { useWorkflowForEditing } from '../hooks/useWorkflowForEditing';
import { BuilderCanvasLazy } from './BuilderCanvasLazy';
import { BuilderBackupBanner } from './BuilderBackupBanner';
import { BuilderHeader } from './BuilderHeader';
import { BuilderInspector } from './BuilderInspector';
import { BuilderPhoneView } from './BuilderPhoneView';
import { ComponentPalette } from './ComponentPalette';
import { PhaseStripLazy } from './PhaseStripLazy';
import { useCanWork } from '@/features/workspaces/canWork';

/**
 * The workflow builder.
 *
 * Three states, disjoint by construction, following `WorkflowGraphPage`: the
 * read is in flight, it failed or named nothing, or there is a workflow. An
 * empty workflow is not one of them — a freshly created draft has no phases and
 * no steps, and that is the *normal* first screen rather than an empty state to
 * apologise for.
 *
 * Selection lives in the URL (`?sel=`), not in component state, so a link to
 * "this workflow, this step" restores exactly this view and a reload keeps the
 * inspector where it was. A stale or unparseable value degrades to no selection
 * rather than failing the route.
 */
export function BuilderPage({
  workspaceId,
  workflowKey,
  selection: rawSelection,
  onSelectionChange,
}: {
  workspaceId: string;
  workflowKey: string;
  selection?: string;
  onSelectionChange: (next: string | undefined) => void;
}) {
  const { source, isPending, loadFailed, refetch } = useWorkflowForEditing(
    workspaceId,
    workflowKey,
  );
  // Bumped by Reload once the fresh read has arrived, so the draft is rebuilt
  // from it even when nothing in the key below moved — a colleague's newer
  // pending changes leave the published version where it was (spec 144).
  const [generation, setGeneration] = useState(0);
  const reload = useCallback(() => {
    void refetch().then(() => setGeneration((current) => current + 1));
  }, [refetch]);
  // The canvas gets the width, and the sidebar remembers this workflow
  // (spec 128 R3, R4).
  useFocusMode();
  useRememberWorkflow(workspaceId, workflowKey);
  const showPlaceholder = usePendingDisplay(isPending);
  // A phone reads the workflow rather than editing it (spec 183).
  const phone = usePhoneWidth();
  // Someone only reading the workspace reads the workflow too (spec 189).
  const canWork = useCanWork();

  // No page frame here to preserve — this component *is* the content region, rendered
  // inside a frame its caller owns. So spec 079's US1 never applied to it; what changes
  // is the shape of the placeholder and that it waits out the appearance delay.
  if (isPending) {
    return showPlaceholder ? (
      <WorkflowSkeleton label="Loading workflow" />
    ) : null;
  }

  if (loadFailed) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3 p-10 text-center">
        <p className="text-muted-foreground text-sm">
          This workflow could not be loaded.
        </p>
        <Button variant="outline" size="sm" onClick={reload}>
          Try again
        </Button>
      </div>
    );
  }

  if (!source) {
    return (
      <NotFoundState
        title="Workflow not found"
        description="This workspace has no workflow with that key. It may have been renamed or removed since the link was shared."
      />
    );
  }

  const workflow = 'workflow' in source ? source.workflow : source;

  if (phone || !canWork) {
    return (
      <BuilderPhoneView
        workspaceId={workspaceId}
        workflow={workflow}
        readOnly={!canWork}
      />
    );
  }

  return (
    // Keyed by id so switching workflows builds a fresh draft rather than
    // carrying one document's unsaved edits — and its undo history — onto
    // another.
    // Keyed by id *and* version: a reload after a conflict re-reads the
    // workflow, and remounting on the new version is what rebuilds the draft
    // from it. Without the version the provider would keep the stale draft it
    // was initialised with, and Reload would appear to do nothing. The
    // generation covers a reload that moves no version: pending changes saved
    // by someone else.
    <DraftProvider
      key={`${workflow.id}:${String(workflow.version)}:${String(generation)}`}
      view={source}
    >
      <BuilderShell
        workspaceId={workspaceId}
        workflowId={workflow.id}
        workflowKey={workflow.key}
        rawSelection={rawSelection}
        onSelectionChange={onSelectionChange}
        onReload={reload}
      />
    </DraftProvider>
  );
}

/**
 * Everything below the provider, so it can read the draft.
 *
 * Split out rather than nested inline because a component cannot consume a
 * context its own render creates.
 */
function BuilderShell({
  workspaceId,
  workflowId,
  workflowKey,
  rawSelection,
  onSelectionChange,
  onReload,
}: {
  workspaceId: string;
  workflowId: string;
  workflowKey: string;
  rawSelection: string | undefined;
  onSelectionChange: (next: string | undefined) => void;
  onReload: () => void;
}) {
  const { draft, edit, undo, redo } = useDraft();

  // Both live here rather than on the canvas: undo is about the document, not
  // about whichever element holds focus, and the guard is about leaving the
  // page rather than leaving the canvas.
  useUndoShortcuts({ onUndo: undo, onRedo: redo });
  // Held here rather than in the header, so clicking a problem — which
  // changes the selection and re-renders the inspector — does not dismiss the
  // list that sent you there.
  const [problems, setProblems] = useState<ImportViolation[]>([]);
  // Which problem the user jumped to, if any. A fresh object per click: the
  // canvas re-centres on identity, so this is what makes clicking the same
  // problem twice work after the user has panned away.
  const [flagged, setFlagged] = useState<ProblemFocus | null>(null);

  // Ordinary selection. Whatever the user just clicked is the subject now, so
  // the mark left by the problem they came from no longer describes where they
  // are.
  const select = useCallback(
    (next: BuilderSelection) => {
      setFlagged(null);
      onSelectionChange(serialiseSelection(next));
    },
    [onSelectionChange],
  );

  // Selection because a problem said so: the same navigation, plus the mark and
  // the viewport move that make the offending element findable at all.
  const selectProblem = useCallback(
    (next: BuilderSelection) => {
      setFlagged({ target: next });
      onSelectionChange(serialiseSelection(next));
    },
    [onSelectionChange],
  );

  // Device preferences, like the shell's own sidebar: how much canvas someone
  // wants is not something that belongs in a shared link.
  const paletteCollapsed = usePreferencesStore(
    (state) => state.paletteCollapsed,
  );
  const togglePalette = usePreferencesStore((state) => state.togglePalette);
  const inspectorCollapsed = usePreferencesStore(
    (state) => state.inspectorCollapsed,
  );
  const toggleInspector = usePreferencesStore((state) => state.toggleInspector);
  const setInspectorCollapsed = usePreferencesStore(
    (state) => state.setInspectorCollapsed,
  );

  // A fresh refusal replaces the list, so the mark from the old one goes too —
  // it would otherwise point at a problem nobody is looking at any more.
  //
  // It also unfolds the inspector, which is the one thing allowed to override
  // that preference: the refusal is listed inside the panel, and answering
  // "why was this refused" with a folded panel is answering nothing. Set rather
  // than toggled, so a refusal arriving at an already-open panel leaves it open.
  const showProblems = useCallback(
    (next: ImportViolation[]) => {
      setProblems(next);
      setFlagged(null);
      setInspectorCollapsed(false);
    },
    [setInspectorCollapsed],
  );

  // Here rather than in the header, because the leave dialog saves too, and
  // its refusal has to land where Save's does (spec 144).
  // Whose browser backup this builder keeps and clears (spec 144 FR-008).
  const owner = useSession().user?.id ?? null;
  const writes = useDraftWrites({
    workspaceId,
    workflowId,
    onProblems: showProblems,
  });
  const unsavedDialog = useUnsavedGuard(draft.dirty, {
    onSaveDraft: writes.save,
    // Left on purpose, so there is nothing to offer back next time.
    onDiscard: () => clearBuilderBackup(owner, workflowId),
  });
  const backup = useBuilderBackup(workflowId);

  // Parsed, then resolved against the draft: a selection naming a step that no
  // longer exists is stale rather than wrong, and it degrades to nothing
  // selected. Both steps live in `selection.ts` and are unit-tested there —
  // this is a derived value with real branching, not a one-liner (Rule 2.3).
  const selection = useMemo<BuilderSelection>(
    () => resolveSelection(draft, parseSelection(rawSelection)),
    [rawSelection, draft],
  );

  // Editing a field withdraws the refusal that was about it — both the mark on
  // the input and its line in the list, which are the same claim shown twice.
  //
  // This is not the client re-validating: nothing here decides the new value is
  // *good*, only that the service's account of the old one no longer describes
  // what is on screen. Leaving it up would mean the panel arguing with text the
  // author is looking at, and the next Save is what actually re-decides.
  const resolveField = useCallback(
    (field: string) => {
      setProblems((current) =>
        current.filter(
          (violation) =>
            violation.field !== field ||
            !violationTargets(violation, selection),
        ),
      );
    },
    [selection],
  );

  const fieldErrors = useMemo(
    () => fieldErrorsFor(problems, selection),
    [problems, selection],
  );

  return (
    <section className="flex flex-1 flex-col gap-4 overflow-hidden p-4">
      <BuilderHeader
        workspaceId={workspaceId}
        workflowId={workflowId}
        workflowKey={workflowKey}
        writes={writes}
        onReload={onReload}
      />

      {backup.offer && (
        <BuilderBackupBanner
          savedAt={backup.offer.savedAt}
          onRestore={backup.restore}
          onThrowAway={backup.throwAway}
        />
      )}

      <PhaseStripLazy selection={selection} onSelect={select} />

      <div className="flex min-h-0 flex-1 gap-3">
        <aside
          className={cn(
            // `overflow-hidden` is what makes the width transition watchable:
            // the palette inside keeps a fixed width for its state, so the
            // labels never reflow as the panel grows — they are revealed by the
            // edge moving, not re-laid-out on every frame. Without it the text
            // wraps into a vertical column and unwraps again, which reads as a
            // glitch rather than as an animation.
            'shrink-0 overflow-x-hidden overflow-y-auto transition-[width]',
            // Wide enough for a 28px control plus its focus ring, and no
            // wider: slack on a rail reads as a gap the user has to cross.
            paletteCollapsed ? 'w-7' : 'w-52',
          )}
        >
          <ComponentPalette
            // Clicking rather than dragging carries no *position* — guessing one
            // would put the node somewhere the user did not choose (FR-011) — but
            // it does carry a phase: US8 puts the step in the selected phase, and
            // only falls back to the holding pen when nothing is selected.
            //
            // A selected *step* deliberately does not count. "The currently
            // selected phase" is a phase the user picked, and inferring one from
            // whatever step happens to be selected would put nodes in phases
            // nobody named.
            onAdd={(type) =>
              edit(
                addStep({
                  type,
                  phaseId:
                    selection?.kind === 'phase' ? selection.id : undefined,
                }),
              )
            }
            collapsed={paletteCollapsed}
            onToggleCollapsed={togglePalette}
          />
        </aside>

        <div className="min-w-0 flex-1">
          <BuilderCanvasLazy
            workspaceId={workspaceId}
            selection={selection}
            onSelect={select}
            flagged={flagged}
          />
        </div>

        <BuilderInspector
          workspaceId={workspaceId}
          selection={selection}
          onSelect={select}
          onSelectProblem={selectProblem}
          problems={problems}
          fieldErrors={fieldErrors}
          onResolveField={resolveField}
          collapsed={inspectorCollapsed}
          onToggleCollapsed={toggleInspector}
        />
      </div>

      {unsavedDialog}
    </section>
  );
}
